import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import omit from "lodash-es/omit";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import ChangePasswordModal from "@app/component/auth/ChangePasswordModal";
import localSettings from "@app/config/LocalSettings";
import ProfileSettingsForm, { UserSettings } from "@app/scene/settings/type/ProfileSettingsForm";
import { getMe, getOrganization, getUserSetting, isMeUpdating } from "@app/store/iam";
import { showChangePasswordModal, updateMe, updateSetting } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import Iam from "@app/types/Iam";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { AVAILABLE_UI_LANGUAGES, USER_IMAGE_FILE_UPLOAD_LIMIT } from "@app/util/consts";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getLocalTimeZoneDisplayName, getLocalTimeZoneFormattedOffset } from "@app/util/dates";
import { getLanguageRegion } from "@app/util/i18n/findLibraryLocale";

import showSaveSettingSuccessfulMessage from "../../util/showSaveSettingSuccessfulMessage";
import ImageUploader from "../common/ImageUploader";
import SettingPage from "../common/SettingPage";

import MainFields from "./component/MainFields";
import styles from "./Profile.module.scss";
import getConfirmationProps from "./utils/getConfirmationProps";

interface Props {
  isMeUpdating: boolean;
  languageSetting: Setting | undefined;
  me: Iam;
  organization: Organization;
  showChangePasswordModal: typeof showChangePasswordModal;
  updateMe: typeof updateMe.request;
  updateSetting: typeof updateSetting.request;
}

const Profile: React.FC<Props> = ({
  isMeUpdating,
  languageSetting,
  me,
  organization,
  showChangePasswordModal,
  updateMe,
  updateSetting,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm<ProfileSettingsForm>();
  const [isDirty, setDirty, unsetDirty] = useBoolean();
  const [languageChanged, setLanguageChanged] = useState(false);

  const handleSubmit = useCallback(async () => {
    const fields = form.getFieldsValue();

    if (languageSetting && languageSetting.value !== fields.language) {
      await new Promise<void>((resolve) =>
        updateSetting({
          callback: () => resolve(),
          orgId: organization.id,
          setting: { ...languageSetting, value: fields.language || null },
          userId: me.id,
        })
      );
    }

    // remove any user-settings fields
    const profileFields = omit(fields, "language");
    updateMe({
      callback: () => {
        if (languageSetting && languageSetting.value !== fields.language) {
          document.location.reload();
        } else {
          showSaveSettingSuccessfulMessage(intl);
          unsetDirty();
        }
      },
      me: { ...me, ...profileFields },
    });
  }, [form, intl, languageSetting, me, organization, unsetDirty, updateMe, updateSetting]);

  const handleUnDo = useCallback(() => {
    form.resetFields();
    unsetDirty();
  }, [form, unsetDirty]);

  const profileAnalytics = useExtendedAnalytics(["Profile"], useAnalytics());

  const handleShowChangePassword = useCallback(() => {
    profileAnalytics.clicked(["Change Password"]);
    showChangePasswordModal();
  }, [profileAnalytics, showChangePasswordModal]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("changePassword")) {
      showChangePasswordModal();
    }
  }, [showChangePasswordModal]);

  let languageValue: UserSettings["language"] = null;
  const languageSettingValue = (languageSetting?.value ?? null) as null | string;
  if (languageSettingValue) {
    const localeParts = getLanguageRegion(languageSettingValue);
    if (localeParts?.language && AVAILABLE_UI_LANGUAGES.includes(localeParts.language)) {
      languageValue = localeParts.language;
    }
  }

  const handleFormChange = useCallback(() => {
    setDirty();
    const formLanguageValue = form.getFieldValue("language");
    setLanguageChanged((formLanguageValue || languageValue) && formLanguageValue !== languageValue);
  }, [form, languageValue, setDirty]);

  const locale = localSettings.getLocale();
  const timezone = `${getLocalTimeZoneDisplayName(locale)} (${getLocalTimeZoneFormattedOffset()})`;

  return (
    <SettingPage
      changesSaving={isMeUpdating}
      hasChanges={isDirty}
      onSaveChanges={form.submit}
      onUnDo={handleUnDo}
      saveConfirmation={languageChanged ? getConfirmationProps(intl, form) : undefined}
      title={intl.formatMessage({
        id: "settings.profile.title",
        defaultMessage: "Profile",
        description: "Title of settings profile page",
      })}
    >
      <AnalyticsContext.Provider value={profileAnalytics}>
        <Form
          form={form}
          initialValues={{
            fullName: me?.fullName,
            language: languageValue || "",
            phone: me?.phone,
            profilePhoto: me?.profilePhoto,
            timezone,
            username: me?.username,
          }}
          layout="vertical"
          onFieldsChange={handleFormChange}
          onFinish={handleSubmit}
        >
          <div className={styles.container}>
            <div className={styles.column}>
              <MainFields />
              <Button onClick={handleShowChangePassword}>
                {intl.formatMessage({
                  id: "settings.profile.showChangePassword",
                  defaultMessage: "Change my password",
                  description: "settings profile page - change password button",
                })}
              </Button>
            </div>
            <div className={styles.column}>
              <ExtendedAnalytics keyPath={["Profile Photo"]}>
                <Form.Item name="profilePhoto" valuePropName="image">
                  <ImageUploader
                    noImageText={intl.formatMessage({
                      id: "settings.profile.imageText",
                      defaultMessage: "Profile photo",
                      description: "Image text of settings profile page",
                    })}
                    sizeLimit={USER_IMAGE_FILE_UPLOAD_LIMIT}
                  />
                </Form.Item>
              </ExtendedAnalytics>
            </div>
          </div>
        </Form>
        <ChangePasswordModal />
      </AnalyticsContext.Provider>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  isMeUpdating: isMeUpdating(state),
  languageSetting: getUserSetting(state)("language"),
  me: getMe(state)!,
  organization: getOrganization(state)!,
});

const mapDispatchToProps = {
  showChangePasswordModal: showChangePasswordModal,
  updateMe: updateMe.request,
  updateSetting: updateSetting.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
